import {
  InventoryAvailabilityMap,
  ProductAvailability,
  ShoppingListAvailabilitySummary,
  ShoppingListItem,
} from '../types/shoppingList';

const OUT_OF_STOCK: ProductAvailability = {
  inStock: false,
  availableQuantity: 0,
  salePrice: null,
};

/**
 * Availability of a single product at the list's supermarket.
 * A product with no inventory record at the supermarket is treated as out of stock.
 */
export function getItemAvailability(
  productId: string,
  inventoryMap: InventoryAvailabilityMap
): ProductAvailability {
  const entry = inventoryMap[productId];
  if (!entry || entry.availableQuantity <= 0) {
    return OUT_OF_STOCK;
  }
  return entry;
}

/**
 * How many list items the supermarket can currently fulfil.
 */
export function summarizeListAvailability(
  items: ShoppingListItem[],
  inventoryMap: InventoryAvailabilityMap
): ShoppingListAvailabilitySummary {
  const totalItems = items.length;
  const availableItems = items.filter(
    (item) => getItemAvailability(item.productId, inventoryMap).inStock
  ).length;
  return {
    totalItems,
    availableItems,
    missingItems: totalItems - availableItems,
  };
}

export interface CartProductsFromList {
  products: ShoppingListItem[];
  skippedProductNames: string[];
  reducedQuantityNames: string[];
}

/**
 * Convert list items into cart product entries, keeping only in-stock products.
 * Quantities are capped at the available stock and prices refreshed to the
 * supermarket's current sale price when known.
 */
export function buildCartProductsFromList(
  items: ShoppingListItem[],
  inventoryMap: InventoryAvailabilityMap
): CartProductsFromList {
  const products: ShoppingListItem[] = [];
  const skippedProductNames: string[] = [];
  const reducedQuantityNames: string[] = [];

  items.forEach((item) => {
    const availability = getItemAvailability(item.productId, inventoryMap);
    if (!availability.inStock) {
      skippedProductNames.push(item.name);
      return;
    }
    const quantity = Math.min(item.quantity, availability.availableQuantity);
    if (quantity < item.quantity) {
      reducedQuantityNames.push(item.name);
    }
    products.push({
      productId: item.productId,
      name: item.name,
      price: availability.salePrice !== null ? availability.salePrice : item.price,
      quantity,
    });
  });

  return { products, skippedProductNames, reducedQuantityNames };
}

/**
 * Merge products built from a shopping list into an existing active cart's
 * product array. Existing entries are topped up (capped at available stock);
 * new products are appended.
 */
export function mergeCartProducts(
  existingProducts: ShoppingListItem[],
  productsToAdd: ShoppingListItem[],
  inventoryMap: InventoryAvailabilityMap
): ShoppingListItem[] {
  const merged = existingProducts.map((product) => ({ ...product }));

  productsToAdd.forEach((productToAdd) => {
    const existingIndex = merged.findIndex((p) => p.productId === productToAdd.productId);
    if (existingIndex === -1) {
      merged.push({ ...productToAdd });
      return;
    }
    const availability = getItemAvailability(productToAdd.productId, inventoryMap);
    const combinedQuantity = merged[existingIndex].quantity + productToAdd.quantity;
    merged[existingIndex] = {
      ...merged[existingIndex],
      price: productToAdd.price,
      // Cap only when live stock is known; a missing inventory record leaves the
      // combined quantity as-is rather than zeroing out the existing cart line.
      quantity: availability.inStock
        ? Math.min(combinedQuantity, availability.availableQuantity)
        : combinedQuantity,
    };
  });

  return merged;
}

const DEFAULT_ADD_QUANTITY = 1;

/**
 * Merge products into an existing shopping list. Matching productIds have
 * quantities summed and name/price refreshed from the incoming entry.
 */
export function mergeListItems(
  existingItems: ShoppingListItem[],
  itemsToAdd: ShoppingListItem[]
): ShoppingListItem[] {
  const merged = existingItems.map((item) => ({ ...item }));

  itemsToAdd.forEach((incoming) => {
    const existingIndex = merged.findIndex((item) => item.productId === incoming.productId);
    if (existingIndex === -1) {
      merged.push({
        productId: incoming.productId,
        name: incoming.name,
        price: incoming.price,
        quantity: incoming.quantity > 0 ? incoming.quantity : DEFAULT_ADD_QUANTITY,
      });
      return;
    }
    merged[existingIndex] = {
      ...merged[existingIndex],
      name: incoming.name,
      price: incoming.price,
      quantity: merged[existingIndex].quantity + (incoming.quantity > 0 ? incoming.quantity : DEFAULT_ADD_QUANTITY),
    };
  });

  return merged;
}
